// web/src/components/PageLayout.tsx
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import TopMenuBar, { Account } from "./TopMenuBar";
import SoundToggle from "./SoundToggle";
import { useViewport } from "../hooks/useViewport";
import { ANDROID_TABLET_MENU_OFFSET } from "../lib/androidLayout";

interface PageLayoutProps {
  account?: Account | null;
  children: React.ReactNode;
  onLogout?: () => void;
  hideNavbar?: boolean; // Hide navbar during game flow (matchmaking, ads, active game)
}

export default function PageLayout({ 
  account, 
  children, 
  onLogout,
  hideNavbar = false
}: PageLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isAndroidTablet = viewport.isAndroidTablet;
  const isHomePage = location.pathname === "/";
  const isPlayPage = location.pathname === "/play";
  const isStorePage = location.pathname === "/store";
  const isLeaderboardPage = location.pathname === "/leaderboard";
  const isAchievementsPage = location.pathname === "/achievements";
  const isTournamentsPage = location.pathname === "/tournaments";
  const isAccountPage = location.pathname === "/account";
  const isContactPage = location.pathname === "/contact";
  const isDeleteAccountPage = location.pathname === "/delete-account";
  const isFullHeightMenuPage = isHomePage || isPlayPage || isStorePage || isLeaderboardPage || isAchievementsPage || isTournamentsPage || isAccountPage || isContactPage || isDeleteAccountPage;
  // Detect if running in browser (not Capacitor native app) - only apply browser fixes to mobile web
  const isBrowser = typeof window !== 'undefined' && !(window as any).Capacitor;
  // Detect Android - when locked to landscape, safe-area-inset-top may not work, so we need a fixed value
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const userAgent = typeof navigator !== 'undefined'
    ? (navigator.userAgent || navigator.vendor || (window as any).opera || '').toLowerCase()
    : '';
  const isIOSDevice = /ipad|iphone|ipod/.test(userAgent);
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  const isIOS = isIOSDevice || isIOSApp;
  
  // Bulletproof iPad detection - handles modern iPads that report as "MacIntel"
  const isIOSPad = (() => {
    if (!isIOS) return false;
    if (typeof navigator === 'undefined') return false;
    
    const ua = userAgent;
    const platform = (navigator as any).platform || '';
    const maxTouchPoints = (navigator as any).maxTouchPoints || 0;
    
    // Explicit iPad in user agent
    if (ua.includes('ipad')) return true;
    
    // Modern iPadOS 13+ reports as MacIntel with touch support
    if (platform === 'MacIntel' && maxTouchPoints > 1) {
      // Additional check: iPad dimensions (both dimensions >= 768)
      const maxDim = Math.max(viewport.width, viewport.height);
      const minDim = Math.min(viewport.width, viewport.height);
      if (maxDim >= 768 && minDim >= 768) return true;
    }
    
    // Fallback: large viewport dimensions suggest iPad
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    if (maxDim >= 768 && minDim >= 768 && isIOS) return true;
    
    return false;
  })();
  
  // Bulletproof iPhone detection - explicitly iPhone, not iPad
  const isIPhone = (() => {
    if (!isIOS) return false;
    if (isIOSPad) return false; // If it's an iPad, it's not an iPhone
    
    const ua = userAgent;
    
    // Explicit iPhone in user agent (but not iPad)
    if (ua.includes('iphone') && !ua.includes('ipad')) return true;
    
    // iPod touch counts as iPhone-like
    if (ua.includes('ipod')) return true;
    
    // If it's iOS app and not iPad, assume iPhone
    if (isIOSApp && !isIOSPad) return true;
    
    // If it's iOS device (from user agent) and not iPad, and viewport is small, assume iPhone
    if (isIOSDevice && !isIOSPad) {
      const maxDim = Math.max(viewport.width, viewport.height);
      const minDim = Math.min(viewport.width, viewport.height);
      // iPhone-like dimensions (smaller than iPad)
      if (maxDim < 768 || minDim < 768) return true;
    }
    
    return false;
  })();
  // Gently nudge the landscape menu down on iOS to clear leaderboard filters height
  const menuYOffset = isMobileLandscape && isIOS ? 48 : 0;
  const useSideMenu = isMobileLandscape && isFullHeightMenuPage;
  
  // Force recalculation on Android when WebView is ready (fixes skinny menu on first load)
  const [forceUpdate, setForceUpdate] = useState(0);
  const androidMenuOffset = isAndroidTablet ? ANDROID_TABLET_MENU_OFFSET : 0;
  const androidMenuScale = isAndroidTablet ? 1.15 : 1; // 15% bigger for Android tablets
  useEffect(() => {
    if (isAndroid && isMobileLandscape) {
      // Wait for WebView to be ready, then force a recalculation
      const handleResize = () => {
        setForceUpdate(prev => prev + 1);
      };
      
      // Listen for resize events
      window.addEventListener('resize', handleResize);
      
      // Also trigger after a short delay to catch late WebView initialization
      const timeout = setTimeout(() => {
        handleResize();
      }, 100);
      
      // Trigger on orientation change
      window.addEventListener('orientationchange', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
        clearTimeout(timeout);
      };
    }
  }, [isAndroid, isMobileLandscape]);
  
  return (
    <div 
      className={(useSideMenu) ? "min-h-screen flex relative" : "max-w-5xl mx-auto"}
      data-page-container="true"
      style={(useSideMenu) ? {
        width: '100vw', // Use 100vw for both web and native app to ensure full width
        maxWidth: '100vw',
        overflowX: 'hidden',
        // Allow a bit of scroll room on mobile web landscape so the address bar can hide
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 48px)',
      } : {}}
    >
      {/* Logo at the very top - hide in mobile landscape */}
      {!(isMobileLandscape && isFullHeightMenuPage) && (
        <div className="flex items-center justify-between px-2 md:px-6 pt-3 pb-2 md:pt-6 md:pb-4">
        <Link 
          to="/" 
          className="text-4xl font-bold hover:opacity-80 transition-opacity" 
          style={{ 
            color: '#3e2615', 
            textShadow: '2px 2px 4px rgba(212, 160, 95, 0.5)', 
            textDecoration: 'none' 
          }}
        >
          Goldrush
        </Link>
        {!account && (
          <button 
            className="text-sm font-medium underline" 
            style={{ color: '#3e2615' }} 
            onClick={() => {
              // Navigate to home page where Gate component will handle showing the modal
              navigate("/", { state: { showRegisterModal: true } });
            }}
          >
            Register
          </button>
        )}
        {account && !account.email_verified && (
          <>
            <div className="flex gap-4">
              <button 
                className="text-sm font-medium underline" 
                style={{ color: '#3e2615' }} 
                onClick={() => {
                  // Navigate to home page where Gate component will handle showing the complete registration modal
                  navigate("/", { state: { showCompleteRegistrationModal: true } });
                }}
              >
                Complete Registration
              </button>
              {onLogout && (
                <button 
                  className="text-sm font-medium underline" 
                  style={{ color: '#3e2615' }} 
                  onClick={onLogout}
                >
                  Log out
                </button>
              )}
            </div>
          </>
        )}
        {account && account.email_verified && onLogout && (
          <button 
            className="text-sm font-medium underline" 
            style={{ color: '#3e2615' }} 
            onClick={onLogout}
          >
            Log out
          </button>
        )}
        </div>
      )}

      {/* Menu bar - vertical on left for mobile landscape home/play/store/leaderboard/tournaments/account/contact page, offset to clear camera punch hole */}
      {account && !hideNavbar && (
        <div 
          className={`${useSideMenu ? 'fixed left-0 top-0 shrink-0 w-[112px] min-w-[112px] max-w-[112px] flex-none' : 'shrink-0 px-2 md:px-6 pb-2 md:pb-4'}`}
          style={useSideMenu ? {
            top: androidMenuOffset ? `${androidMenuOffset}px` : undefined,
            paddingTop: isAndroid && isMobileLandscape
              ? '40px'
              : (isIOS ? `calc(env(safe-area-inset-top, 24px) + 8px + ${menuYOffset}px${isIOSPad ? ' + 12px' : ''})` : `calc(env(safe-area-inset-top, 24px) + 8px)`),
            // Web: modestly widen and nudge left. Native/Android: narrower and shifted right off the edge.
            left: isIOSPad
              ? 'calc(env(safe-area-inset-left, 0px) + 12px)'
              : (isBrowser ? '4px' : `calc(env(safe-area-inset-left, 0px) + ${isAndroidTablet ? 26 : 32}px)`),
            width: isBrowser ? '76px' : (isAndroidTablet ? '87.4px' : isIOSPad ? '91.2px' : '76px'), // 15% bigger for Android tablets (76 * 1.15) / 20% bigger for iPad
            minWidth: isBrowser ? '76px' : (isAndroidTablet ? '87.4px' : isIOSPad ? '91.2px' : '76px'),
            flexShrink: 0,
            zIndex: 50, // Higher than page content - overlays (z-[100]) will cover everything
            display: 'flex',
            flexDirection: 'column',
            bottom: 'calc(env(safe-area-inset-bottom, 8px) + 6px)', // This controls where the menu bar stops - adjust this value to move it up/down
            // iPhone: move menu left and down slightly
            transform: isIPhone ? 'translate(-30px, -20px)' : undefined,
            ...(isBrowser && {
              maxHeight: 'calc(100vh - env(safe-area-inset-top, 24px) - 8px - env(safe-area-inset-bottom, 8px) - 6px - 50px)', // Browser only: prevent menu bar from growing too tall
            }),
          } : {
            flexShrink: 0,
          }}>
          <TopMenuBar account={account} onLogout={onLogout} />
          {/* Complete Registration link in nav column (mobile landscape) */}
          {account && !account.email_verified && isMobileLandscape && !hideNavbar && (
            <div className="mt-auto pb-2 flex justify-center">
              {(isHomePage || isPlayPage || isStorePage || isLeaderboardPage || isTournamentsPage || isAccountPage || isContactPage || isDeleteAccountPage) && (
                <button
                  className="text-xs font-semibold underline text-center px-1 whitespace-nowrap"
                  style={{ 
                    color: (isHomePage || isPlayPage || isStorePage) ? '#fffaeb' : (isAndroid ? '#fffaeb' : '#3e2615'),
                    textShadow: (isHomePage || isPlayPage || isStorePage || isAndroid) ? '0 0 3px rgba(0,0,0,0.55)' : undefined,
                    display: 'block'
                  }}
                  onClick={() => {
                    navigate("/", { state: { showCompleteRegistrationModal: true } });
                  }}
                >
                  Complete<br/>Registration
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Complete Registration link - portrait/regular mobile (dark text) */}
      {account && !account.email_verified && viewport.isMobile && !isMobileLandscape && !isHomePage && !isStorePage && !isPlayPage && !hideNavbar && (
        <div className="fixed left-0 z-50" style={{ 
          paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 32px)',
          width: '112px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          bottom: isAndroid ? 'calc(env(safe-area-inset-bottom, 8px) + 16px)' : 'calc(env(safe-area-inset-bottom, 8px) + 16px)', // Position link with more space from bottom edge
          height: '32px'
        }}>
          <button 
            className="text-xs font-medium underline text-left px-2 whitespace-nowrap" 
            style={{ color: '#3e2615' }}
            onClick={() => {
              navigate("/", { state: { showCompleteRegistrationModal: true } });
            }}
          >
            Complete Registration
          </button>
        </div>
      )}


      {/* Contact, Privacy, Logout links - bottom right, one line, aligned with enter mine button bottom - only on home page */}
      {account && isMobileLandscape && isHomePage && !isPlayPage && (
        <div className="fixed right-0 z-50 flex flex-row gap-3 items-end justify-end" style={{ 
          paddingRight: 'calc(env(safe-area-inset-right, 0px) + 24px)',
          bottom: isIOSPad ? 'calc(env(safe-area-inset-bottom, 8px) + 28px)' : (isBrowser ? 'max(calc(env(safe-area-inset-bottom, 8px) - 32px), 8px)' : 'calc(env(safe-area-inset-bottom, 8px) - 32px)'),
          height: '32px'
        }}>
          <Link
            to="/contact"
            className="text-xs underline hover:opacity-80 transition-opacity whitespace-nowrap"
            style={{ color: '#fffaeb', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
          >
            Contact
          </Link>
          <Link
            to="/privacy"
            className="text-xs underline hover:opacity-80 transition-opacity whitespace-nowrap"
            style={{ color: '#fffaeb', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
          >
            Privacy Policy
          </Link>
          {onLogout && (
            <button
              onClick={onLogout}
              className="text-xs underline hover:opacity-80 transition-opacity whitespace-nowrap"
              style={{ color: '#fffaeb', textShadow: '1px 1px 2px rgba(0,0,0,0.5)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginRight: '0' }}
            >
              Log out
            </button>
          )}
        </div>
      )}

      {/* Page content - add left margin for mobile landscape (but not for play page level selector which is fixed) */}
      <div className={useSideMenu && (isHomePage || isLeaderboardPage || isTournamentsPage || isAccountPage || isContactPage || isDeleteAccountPage) ? "flex-1 ml-28 relative z-10" : (useSideMenu && (isPlayPage || isStorePage) ? "relative z-10" : "")}>
        {children}
      </div>

      {/* Footer with copyright, contact link, and sound toggle - hide in mobile landscape on home/play/store/leaderboard/tournaments/account/contact page */}
      {!(isMobileLandscape && isFullHeightMenuPage) && (
        <div className="flex items-center justify-between px-2 md:px-6 pt-2 pb-1 md:pt-4 md:pb-2">
          <div className="flex items-center gap-4 text-sm" style={{ color: '#3e2615' }}>
            <span>© Slantwise.ai</span>
            <Link 
              to="/contact" 
              className="underline hover:opacity-80 transition-opacity"
              style={{ color: '#3e2615' }}
            >
              Contact
            </Link>
            <Link 
              to="/privacy" 
              className="underline hover:opacity-80 transition-opacity"
              style={{ color: '#3e2615' }}
            >
              Privacy Policy
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://play.google.com/store/apps/details?id=ai.goldrush.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-90 transition-opacity"
            >
              <img
                src={`${import.meta.env.BASE_URL}GetItOnGooglePlay_Badge_Web_color_English.png`}
                alt="Get it on Google Play"
                className="h-10 w-auto"
              />
            </a>
            <SoundToggle />
          </div>
        </div>
      )}
    </div>
  );
}
